// ============================================
// SERVICE CENTRALISÉ DE CLÔTURE DE CAISSE
// Logique unique, réutilisable, temps réel
// ============================================

import { toast } from 'sonner';

export const COINS = [25, 50, 100, 200];
export const BILLS = [500, 1000, 2000, 5000, 10000];
export const TOLERANCE_THRESHOLD = 500;
export const LARGE_VARIANCE_THRESHOLD = 5000;

// Permissions centralisées pour la clôture
export const CLOSING_PERMISSIONS = {
  canCreateClosing: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CAISSIER', 'SERVEUR'],
  canViewAllClosings: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
  canValidateClosing: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'],
  canRejectClosing: ['ADMIN', 'DIRECTEUR'],
  canDeleteClosing: ['ADMIN'],
  canViewOwnClosing: ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CAISSIER', 'SERVEUR', 'BARMAN', 'CUISINIER']
};

// Vérifier si un rôle a une permission
export const hasClosingPermission = (role, permission) => {
  const normalizedRole = (role || '').toUpperCase();
  const allowedRoles = CLOSING_PERMISSIONS[permission] || [];
  return allowedRoles.includes(normalizedRole);
};

// Formater en CFA
export const formatCFA = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
};

// Calculer le total des pièces
export const calculateCoinsTotal = (coinCounts) => {
  return COINS.reduce((sum, d) => sum + (d * (coinCounts[d] || 0)), 0);
};

// Calculer le total des billets
export const calculateBillsTotal = (billCounts) => {
  return BILLS.reduce((sum, d) => sum + (d * (billCounts[d] || 0)), 0);
};

// Calculer le statut de l'écart
export const getVarianceStatus = (variance, countedTotal) => {
  const varianceAbs = Math.abs(variance);
  
  if (countedTotal === 0) {
    return { status: 'pending', label: 'En attente', color: 'gray' };
  }
  if (varianceAbs === 0) {
    return { status: 'exact', label: 'Caisse exacte', color: 'emerald' };
  }
  if (varianceAbs <= TOLERANCE_THRESHOLD) {
    return { 
      status: 'minor', 
      label: variance > 0 ? 'Léger excédent' : 'Léger manquant', 
      color: 'emerald' 
    };
  }
  if (varianceAbs < LARGE_VARIANCE_THRESHOLD) {
    return { 
      status: 'warning', 
      label: variance > 0 ? 'Excédent' : 'Manquant', 
      color: 'amber' 
    };
  }
  return { 
    status: 'critical', 
    label: variance > 0 ? 'Excédent important' : 'Manquant important', 
    color: 'red' 
  };
};

// Service de clôture centralisé
class CashClosingService {
  constructor() {
    this.listeners = new Set();
    this.pendingSaves = new Map();
  }

  // S'abonner aux mises à jour
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Notifier tous les abonnés
  notify(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (e) {
        console.error('CashClosingService notification error:', e);
      }
    });
  }

  // Charger les données théoriques
  async loadTheoretical(api, registerId) {
    try {
      const response = await api().get(`/cash-closing/theoretical/${registerId}`);
      return { success: true, data: response.data || response };
    } catch (error) {
      console.error('Error loading theoretical:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de chargement',
        data: {
          starting_cash: 50000,
          cash_sales: 0,
          theoretical_cash: 50000,
          mobile_money_sales: 0,
          card_sales: 0,
          airtel_money_sales: 0,
          moov_money_sales: 0,
          orders_count: 0,
          total_sales: 0
        }
      };
    }
  }

  // Charger le détail des ventes
  async loadSalesDetail(api, registerId) {
    try {
      const response = await api().get(`/cash-closing/my-sales/${registerId}`);
      return { success: true, data: response.data || response };
    } catch (error) {
      console.error('Error loading sales detail:', error);
      return { success: false, error: 'Erreur de chargement des ventes' };
    }
  }

  // Créer une clôture
  async createClosing(api, data) {
    const saveId = Date.now().toString();
    this.pendingSaves.set(saveId, { status: 'saving', data });
    
    try {
      const payload = {
        register_id: data.registerId,
        session_id: data.sessionId,
        coins: COINS.map(d => ({ denomination: d, quantity: data.coinCounts[d] || 0 })),
        bills: BILLS.map(d => ({ denomination: d, quantity: data.billCounts[d] || 0 })),
        notes: data.notes || '',
        recount_number: data.recountNumber || 0,
        theoretical_cash: data.theoreticalCash,
        counted_cash: data.countedTotal,
        variance: data.variance,
        card_sales: data.cardSales || 0,
        airtel_money_sales: data.airtelSales || 0,
        moov_money_sales: data.moovSales || 0
      };

      const response = await api().post('/cash-closing/create', payload);
      
      this.pendingSaves.set(saveId, { status: 'saved', data: response.data });
      this.notify('closing_created', response.data);
      
      return { success: true, data: response.data };
    } catch (error) {
      this.pendingSaves.set(saveId, { status: 'error', error });
      console.error('Error creating closing:', error);
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur lors de l\'enregistrement'
      };
    } finally {
      setTimeout(() => this.pendingSaves.delete(saveId), 5000);
    }
  }

  // Valider une clôture
  async validateClosing(api, closingId, approved, notes = '') {
    try {
      const response = await api().post(`/cash-closing/${closingId}/validate`, {
        approved,
        notes
      });
      
      this.notify('closing_validated', { closingId, approved, data: response.data });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error validating closing:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de validation'
      };
    }
  }

  // Obtenir le statut d'enregistrement en cours
  getSaveStatus(saveId) {
    return this.pendingSaves.get(saveId);
  }

  // Vérifier s'il y a des sauvegardes en cours
  hasPendingSaves() {
    return Array.from(this.pendingSaves.values()).some(s => s.status === 'saving');
  }
}

// Instance singleton
export const cashClosingService = new CashClosingService();

// Hook personnalisé pour utiliser le service
export const useCashClosingService = () => {
  return {
    service: cashClosingService,
    formatCFA,
    calculateCoinsTotal,
    calculateBillsTotal,
    getVarianceStatus,
    hasClosingPermission,
    COINS,
    BILLS,
    TOLERANCE_THRESHOLD,
    LARGE_VARIANCE_THRESHOLD
  };
};

export default cashClosingService;
